import type { HomeworkItem, RecurringItem, Class } from '@homework-app/shared';

const DB_NAME = 'HomeworkAppDB';
const DB_VERSION = 2;
const HOMEWORK_STORE = 'homework';
const RECURRING_STORE = 'recurring';
const CLASSES_STORE = 'classes';
const SYNC_QUEUE_STORE = 'syncQueue';
const PHOTOS_STORE = 'photos';

export interface SyncQueueItem {
  id: string;
  action: 'create' | 'update' | 'delete';
  type: 'homework' | 'recurring' | 'photo' | 'classes';
  data: any;
  timestamp?: number;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Homework items store - keyed by date
        if (!db.objectStoreNames.contains(HOMEWORK_STORE)) {
          const homeworkStore = db.createObjectStore(HOMEWORK_STORE, { keyPath: 'dateKey' });
          homeworkStore.createIndex('dateKey', 'dateKey', { unique: true });
        }

        // Recurring homework store
        if (!db.objectStoreNames.contains(RECURRING_STORE)) {
          db.createObjectStore(RECURRING_STORE, { keyPath: 'id', autoIncrement: true });
        }

        // Classes store
        if (!db.objectStoreNames.contains(CLASSES_STORE)) {
          db.createObjectStore(CLASSES_STORE, { keyPath: 'period' });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
          const syncStore = db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id' });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Photos store (for offline photo data)
        if (!db.objectStoreNames.contains(PHOTOS_STORE)) {
          const photoStore = db.createObjectStore(PHOTOS_STORE, { keyPath: 'id' });
          photoStore.createIndex('homeworkId', 'homeworkId', { unique: false });
        }
      };
    });
  }

  private ensureDB(): IDBDatabase {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  // Homework operations
  async getHomeworkForDate(dateKey: string): Promise<{ [period: number]: HomeworkItem[] } | null> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([HOMEWORK_STORE], 'readonly');
      const store = transaction.objectStore(HOMEWORK_STORE);
      const request = store.get(dateKey);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveHomeworkForDate(dateKey: string, data: { [period: number]: HomeworkItem[] }): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([HOMEWORK_STORE], 'readwrite');
      const store = transaction.objectStore(HOMEWORK_STORE);
      const request = store.put({ dateKey, data });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Recurring homework operations
  async getRecurringHomework(): Promise<RecurringItem[]> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([RECURRING_STORE], 'readonly');
      const store = transaction.objectStore(RECURRING_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async saveRecurringHomework(items: RecurringItem[]): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([RECURRING_STORE], 'readwrite');
      const store = transaction.objectStore(RECURRING_STORE);

      // Clear existing and add new
      store.clear();
      items.forEach(item => store.add(item));

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Classes operations
  async getClasses(): Promise<Class[]> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CLASSES_STORE], 'readonly');
      const store = transaction.objectStore(CLASSES_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const result = request.result || [];
        // Return stored classes or default if empty
        if (result.length === 0) {
          resolve([]);
        } else {
          resolve(result.sort((a: Class, b: Class) => a.period - b.period));
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveClasses(classes: Class[]): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CLASSES_STORE], 'readwrite');
      const store = transaction.objectStore(CLASSES_STORE);

      store.clear();
      classes.forEach(cls => {
        store.add(cls);
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Sync queue operations
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp'>): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);

      const queueItem: SyncQueueItem = {
        ...item,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: Date.now()
      };

      const request = store.add(queueItem);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readonly');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async clearSyncQueue(): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async removeSyncQueueItem(id: string): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Photo operations
  async savePhoto(id: string, homeworkId: string, blob: Blob): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTOS_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTOS_STORE);
      const request = store.put({ id, homeworkId, blob, timestamp: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getPhoto(id: string): Promise<Blob | null> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTOS_STORE], 'readonly');
      const store = transaction.objectStore(PHOTOS_STORE);
      const request = store.get(id);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.blob : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deletePhoto(id: string): Promise<void> {
    const db = this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTOS_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTOS_STORE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Utility to clear all data
  async clearAll(): Promise<void> {
    const db = this.ensureDB();
    const storeNames = [HOMEWORK_STORE, RECURRING_STORE, CLASSES_STORE, SYNC_QUEUE_STORE, PHOTOS_STORE];

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeNames, 'readwrite');

      storeNames.forEach(storeName => {
        transaction.objectStore(storeName).clear();
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorage();
